import { useQuery } from 'data-fetching-client';
import { useMemo, useRef } from 'react';
import { GET_PURCHASE_OPTIONS } from 'pricing-pages-api/purchase-options/queries';
import { useAppSelector } from '../reducers';
import { planBuilderCheckoutProductConfigurationsSelector } from '../selectors/PlanBuilderSelectors';
import { convertSkuIdsToSkuUuidsForProductConfigurationsSelector } from '../selectors/ProductConfigsSelectors';
import { skuIdIsQuantityPackSelector, skuUuidForSkuIdSelector } from '../selectors/ProductsSelectors';
import { useGlobalLoading } from './loadingHooks';
import { useQueryVariables } from './useBulkPurchaseOptions';
const usePurchaseOptions = ({
  products = [],
  term,
  skip,
  onCompleted,
  onError
}) => {
  const defaultQueryVariables = useQueryVariables();
  const isAppReady = useGlobalLoading();
  return useQuery(GET_PURCHASE_OPTIONS, {
    variables: Object.assign({
      products,
      term
    }, defaultQueryVariables),
    onCompleted,
    onError,
    skip: skip || !((products === null || products === void 0 ? void 0 : products.length) > 0) || !isAppReady || !defaultQueryVariables.currencyCode || !defaultQueryVariables.language
  });
};
export const usePurchaseOptionsForProducts = ({
  productConfigurations,
  term,
  skip,
  onCompleted,
  onError
}) => {
  var _data$purchaseOptions, _data$purchaseOptions2;
  const isAppReady = useGlobalLoading();
  const products = useAppSelector(state => {
    if (isAppReady) {
      return convertSkuIdsToSkuUuidsForProductConfigurationsSelector(state, {
        productConfigurations
      });
    }
  });
  const {
    data,
    loading,
    error
  } = usePurchaseOptions({
    products,
    term,
    skip,
    onCompleted,
    onError
  });
  return {
    loading,
    error,
    purchaseOptions: data === null || data === void 0 || (_data$purchaseOptions = data.purchaseOptions) === null || _data$purchaseOptions === void 0 ? void 0 : _data$purchaseOptions.purchaseOptions,
    hasCommonTerms: data === null || data === void 0 || (_data$purchaseOptions2 = data.purchaseOptions) === null || _data$purchaseOptions2 === void 0 ? void 0 : _data$purchaseOptions2.hasCommonTerms
  };
};
export const usePurchaseOptionsForProductsForTerm = ({
  productConfigurations,
  term = 'annual',
  skip,
  onCompleted,
  onError
}) => {
  // using a ref so the callback doesn't have stale data
  const termRef = useRef(term);
  termRef.current = term;
  const {
    purchaseOptions,
    hasCommonTerms,
    loading,
    error
  } = usePurchaseOptionsForProducts({
    productConfigurations,
    term,
    skip,
    onCompleted: data => {
      var _data$purchaseOptions3, _data$purchaseOptions4;
      const purchaseOptionToReturn = (data === null || data === void 0 || (_data$purchaseOptions3 = data.purchaseOptions) === null || _data$purchaseOptions3 === void 0 || (_data$purchaseOptions3 = _data$purchaseOptions3.purchaseOptions) === null || _data$purchaseOptions3 === void 0 ? void 0 : _data$purchaseOptions3.find(current => current.offerInfo.payment === termRef.current)) || (data === null || data === void 0 || (_data$purchaseOptions4 = data.purchaseOptions) === null || _data$purchaseOptions4 === void 0 || (_data$purchaseOptions4 = _data$purchaseOptions4.purchaseOptions) === null || _data$purchaseOptions4 === void 0 ? void 0 : _data$purchaseOptions4[0]);
      if (onCompleted) {
        onCompleted({
          pricingData: purchaseOptionToReturn,
          hasCommonTerms: data.purchaseOptions.hasCommonTerms
        });
      }
    },
    onError
  });
  const purchaseOptionToReturn = useMemo(() => {
    return (purchaseOptions === null || purchaseOptions === void 0 ? void 0 : purchaseOptions.find(current => {
      return current.offerInfo.payment === term;
    })) || (purchaseOptions === null || purchaseOptions === void 0 ? void 0 : purchaseOptions[0]);
  }, [purchaseOptions, term]);
  return {
    loading,
    error,
    pricingData: purchaseOptionToReturn,
    hasCommonTerms
  };
};

/**
 * @description This hook is only intended for use within the Plan Builder component on the Create a Bundle page. It cannot be used for other components.
 *
 * @param term - The term to get the price for
 * @param onCompleted - The callback to call when the data is loaded
 * @param onError - The callback to call when the data fails to load
 * @returns The pricing data for the term to display within the Plan Builder component
 */
export const usePlanBuilderPurchaseOptions = ({
  term,
  onCompleted,
  onError
}) => {
  const checkoutProductConfigurations = useAppSelector(planBuilderCheckoutProductConfigurationsSelector);
  const productConfigurationsWithoutQuantityPacks = useAppSelector(state => checkoutProductConfigurations.filter(({
    skuId
  }) => !skuIdIsQuantityPackSelector(state, {
    skuId
  })));
  const {
    pricingData,
    hasCommonTerms,
    loading,
    error
  } = usePurchaseOptionsForProductsForTerm({
    productConfigurations: productConfigurationsWithoutQuantityPacks,
    term,
    onCompleted,
    onError
  });
  return {
    loading,
    error,
    pricingData,
    hasCommonTerms
  };
};

/**
 * @description Helper hook to get the pricing data for a single product within a list of product configurations. This should not be exported for use directly by components, rather components should use hooks to get specific pieces of information (e.g. useMonthlyDiscountedPriceForTerm)
 *
 */
const usePricingDataForProductInProductConfigurations = ({
  productConfiguration,
  allProductConfigurations,
  term
}) => {
  const productSkuUuid = useAppSelector(state => skuUuidForSkuIdSelector(state, {
    skuId: productConfiguration.skuId
  }));
  const {
    pricingData,
    loading,
    error
  } = usePurchaseOptionsForProductsForTerm({
    productConfigurations: allProductConfigurations,
    term
  });
  const productPricingData = useMemo(() => {
    var _pricingData$perProdu;
    return pricingData === null || pricingData === void 0 || (_pricingData$perProdu = pricingData.perProductPricing) === null || _pricingData$perProdu === void 0 ? void 0 : _pricingData$perProdu[productSkuUuid];
  }, [pricingData, productSkuUuid]);
  return {
    pricingData: productPricingData,
    loading,
    error
  };
};

/**
 * @example
 * const { monthlyDiscountedPrice, loading, error } = useMonthlyDiscountedPriceForTerm({
 *   productConfiguration: { skuId: 123, quantity: 1 }, // marketing-hub-starter
 *   allProductConfigurations: [
 *     { skuId: 123, quantity: 1 }, // marketing-hub-starter
 *     { skuId: 456, quantity: 1 }, // sales-hub-starter
 *     { skuId: 789, quantity: 1 }, // service-hub-starter
 *     { skuId: 101, quantity: 1 }, // cms-hub-starter
 *     { skuId: 201, quantity: 1 }, // operations-hub-starter
 *   ],
 *   term: 'annual',
 * });
 *
 * <span>The price for Marketing Hub Starter with the Starter Customer Platform discount is ${monthlyDiscountedPrice}</span>
 *
 * @example
 * const { monthlyDiscountedPrice, loading, error } = useMonthlyDiscountedPriceForTerm({
 *   productConfiguration: { skuId: 999, quantity: 1 }, // HubSpot for Marketers Pro
 *   term: 'annual',
 * });
 *
 * <span>The price for HubSpot for Marketers Pro with an annual commitment is ${monthlyDiscountedPrice}</span>
 *
 * @description This hook is useful for getting the price of a single product (productConfiguration) and especially useful if you have multiple products being purchased together (allProductConfigurations) and need to show the price for each product.
 *
 * @param productConfiguration - The product configuration to get the monthly discounted price for
 * @param allProductConfigurations - All product configurations to consider, one of which is productConfiguration
 * @param term - annual or monthly
 * @returns The monthly discounted price for a specified product within the context of
 * allProductConfigurations
 */
export const useMonthlyDiscountedPriceForTerm = ({
  productConfiguration,
  allProductConfigurations = [productConfiguration],
  term
}) => {
  const {
    pricingData,
    loading,
    error
  } = usePricingDataForProductInProductConfigurations({
    productConfiguration,
    allProductConfigurations,
    term
  });

  // Handles both recurring and non-recurring data shape
  const monthlyDiscountedPrice = useMemo(() => {
    const discountedPrice = pricingData === null || pricingData === void 0 ? void 0 : pricingData.discountedPrice;
    const monthlyPriceStr = discountedPrice ? 'monthlyPrice' in discountedPrice ? discountedPrice.monthlyPrice : discountedPrice.price : undefined;
    return monthlyPriceStr ? Number(monthlyPriceStr) : undefined;
  }, [pricingData]);
  return {
    monthlyDiscountedPrice,
    loading,
    error
  };
};

/**
 * @description This hook is similar to useMonthlyDiscountedPriceForTerm, but it returns the due upon purchase price instead of the monthly discounted price.
 *
 * @param productConfiguration - The product configuration to get the monthly discounted price for
 * @param allProductConfigurations - All product configurations to consider, one of which is productConfiguration
 * @param term - annual or monthly
 * @returns The monthly discounted price for a specified product within the context of
 * allProductConfigurations
 */
export const useDueUponPurchaseDiscountedPriceForTerm = ({
  productConfiguration,
  allProductConfigurations = [productConfiguration],
  term
}) => {
  const {
    pricingData,
    loading,
    error
  } = usePricingDataForProductInProductConfigurations({
    productConfiguration,
    allProductConfigurations,
    term
  });

  // Handles both recurring and non-recurring data shape
  const dueUponPurchaseDiscountedPrice = useMemo(() => {
    const discountedPrice = pricingData === null || pricingData === void 0 ? void 0 : pricingData.discountedPrice;
    const dueUponPurchaseStr = discountedPrice ? 'dueUponPurchase' in discountedPrice ? discountedPrice.dueUponPurchase : discountedPrice.price : undefined;
    return dueUponPurchaseStr ? Number(dueUponPurchaseStr) : undefined;
  }, [pricingData]);
  return {
    dueUponPurchaseDiscountedPrice,
    loading,
    error
  };
};
/**
 * Returns common terms for multiple products using their skuUuids.
 *
 * @param productSkuUuids - Array of product skuUuids
 * @param queryParamTerm - Optional term preference ('monthly' or 'annual')
 * @returns Common commitment and payment terms, or null if no common terms exist
 */
export const usePurchaseOptionsCommonTerms = ({
  productSkuUuids,
  queryParamTerm = 'monthly'
}) => {
  var _data$purchaseOptions5, _data$purchaseOptions6, _data$purchaseOptions7;
  const {
    data,
    loading,
    error
  } = usePurchaseOptions({
    products: productSkuUuids.map(skuUuid => ({
      skuUuid
    })),
    term: queryParamTerm
  });
  const recurringOptions = data === null || data === void 0 || (_data$purchaseOptions5 = data.purchaseOptions) === null || _data$purchaseOptions5 === void 0 ? void 0 : _data$purchaseOptions5.recurringOptions;
  const hasCommonTerms = (_data$purchaseOptions6 = data === null || data === void 0 || (_data$purchaseOptions7 = data.purchaseOptions) === null || _data$purchaseOptions7 === void 0 ? void 0 : _data$purchaseOptions7.hasCommonTerms) !== null && _data$purchaseOptions6 !== void 0 ? _data$purchaseOptions6 : false;
  const commonTerms = useMemo(() => {
    if (!(recurringOptions !== null && recurringOptions !== void 0 && recurringOptions.length) || !hasCommonTerms) {
      return null;
    }

    // convert query param term to number for comparison (1 = monthly, 12 = annual)
    const queryParamTermNumber = queryParamTerm.toUpperCase() === 'ANNUAL' ? 12 : 1;

    // find the purchase option matching the requested term preference
    const matchingOption = recurringOptions.find(option => option.paymentTerm === queryParamTermNumber) || recurringOptions[0];
    const commitmentTerm = matchingOption.commitmentTerm === 12 ? 'ANNUAL' : 'MONTHLY';
    const paymentTerm = matchingOption.paymentTerm === 12 ? 'ANNUAL' : 'MONTHLY';
    return {
      commitmentTerm,
      paymentTerm
    };
  }, [recurringOptions, hasCommonTerms, queryParamTerm]);
  return {
    commonTerms,
    hasCommonTerms,
    loading,
    error
  };
};